import React, { useState } from 'react'

import { loadBusinessSubscription } from '@disco/state-management/actions/UserActions'
import captureError from '@disco/utils/captureError'
import { openChargebeePortal } from '@disco/utils/chargebee'
import { pushUINotification } from '@disco/utils/notifications'

import type { HttpErrorType } from 'types/errors'

import { ADMIN_ONLY_MESSAGE } from 'components/common/your-subscription/utils'
import { Button, Flex, Text, Tooltip } from 'components/ds'

import { useDispatch } from 'hooks/use-redux'

const SCRIPT_FAILED_MESSAGE =
  'Billing could not be loaded. Please refresh the page and try again.'

const getErrorMessage = (error: Partial<HttpErrorType>): string | null => {
  if (error.status === 403) return error.body?.error ?? null
  if (error.body?.error_code === 'no_subscription')
    return error.body.error_msg ?? null

  return null
}

/** Opens the Chargebee self-serve portal; shows an error toast if it can't open. */
export function ManageBillingButton({
  disabled,
  hasChargebeeScriptLoaded,
  hasChargebeeScriptFailed,
}: {
  disabled: boolean
  hasChargebeeScriptLoaded: boolean
  hasChargebeeScriptFailed: boolean
}) {
  const dispatch = useDispatch()
  const [isOpeningPortal, setIsOpeningPortal] = useState(false)

  const openPortal = async () => {
    setIsOpeningPortal(true)

    try {
      await openChargebeePortal(() => {
        dispatch(loadBusinessSubscription())
      })
    } catch (error) {
      const message = getErrorMessage(error)

      if (!message) {
        captureError(error, {
          description: 'openPortal error in ManageBillingButton',
        })
      }

      pushUINotification({
        message:
          message ??
          'Billing could not be opened due to an error. Please try again.',
        level: 'error',
      })
    } finally {
      setIsOpeningPortal(false)
    }
  }

  const isDisabled = disabled || !hasChargebeeScriptLoaded
  const tooltip = disabled
    ? ADMIN_ONLY_MESSAGE
    : hasChargebeeScriptFailed
    ? SCRIPT_FAILED_MESSAGE
    : null

  // aria-disabled, not disabled: keeps it focusable and hoverable for the tooltip
  const button = (
    <Button
      variant="soft"
      color="gray"
      size="2"
      aria-disabled={isDisabled}
      data-disabled={isDisabled || isOpeningPortal || undefined}
      loading={isOpeningPortal}
      onClick={isDisabled ? undefined : openPortal}
    >
      <Text size="2">Manage billing</Text>
    </Button>
  )

  return (
    <Flex>
      {tooltip ? <Tooltip content={tooltip}>{button}</Tooltip> : button}
    </Flex>
  )
}
